import React from 'react';
import _ from 'underscore';

import strUtil from 'util/strUtil';
import {Optional} from 'util/Types';

import {getOptionDataPureTextName, OptionData} from './MenuConstants';
import OptionContent from './OptionContent';

export const DefaultOptionContent: React.FC<{optionData: OptionData; className?: string}> = (props) => (
  <OptionContent
    icon={props.optionData.icon}
    iconClassName={props.optionData.iconClassName}
    hasSubmenu={Boolean(props.optionData.submenu)}
    className={props.className}
    tooltipTitle={
      props.optionData.tooltipTitle ??
      (props.optionData.disabled ? props.optionData.disabledMessage : undefined)
    }
    dataTestAttrs={props.optionData.dataTestAttrs ?? {component: 'DefaultOptionContent', element: 'root'}}
    metadata={props.optionData.metadata}
  >
    {props.optionData.name}
  </OptionContent>
);

export function defaultRenderOptionContent(optionData: OptionData): JSX.Element {
  return <DefaultOptionContent optionData={optionData} />;
}

export function isOptionSelectable(optionData: OptionData): boolean {
  return optionData.hasOwnProperty('value') && !optionData.isHeader && !optionData.disabled;
}

export function getSelectableIndices(optionDatas: Array<OptionData>): Array<number> {
  const selectableIndices: Array<number> = [];
  optionDatas.forEach((optionData: OptionData, index: number) => {
    if (isOptionSelectable(optionData)) {
      selectableIndices.push(index);
    }
  });
  return selectableIndices;
}

export function hoverToSelectableIndex(selectableIndices: Array<number>, hoverIndex: number): number {
  return selectableIndices[hoverIndex];
}

export function getOptionRefForIndex(index: number): string {
  return `scrollOption${index}`;
}

/**
 * Filters out the submenu sections according to @param mapSubmenuSection
 */
export function optionDatasWithSubmenuTransform(
  optionDatas: Array<OptionData>,
  mapSubmenuSectionFn: (submenuSection: Array<Optional<OptionData>>) => Array<Optional<OptionData>>
): Array<OptionData> {
  return optionDatas.map((optionData) =>
    optionData.submenu
      ? {
          ...optionData,
          submenu: optionData.submenu.map((submenuSection: Array<Optional<OptionData>>) =>
            mapSubmenuSectionFn(submenuSection)
          ),
        }
      : optionData
  );
}

/**
 * Returns the given option datas, but with the top-level menu and each of the submenus sorted such that
 * options which match the partitionFunc comes before those that don't.
 * @param optionDatas - the original option datas, which may include submenus
 * @param partitionFunc - the function by which to sort
 */
export function partitionOptionDatas(
  optionDatas: Array<OptionData>,
  partitionFunc: (optionData: OptionData) => boolean
): Array<OptionData> {
  const [matchingOptions, notMatchingOptions] = _.partition(optionDatas, partitionFunc);
  const partitionedToplevelOptions = [...matchingOptions, ...notMatchingOptions];
  return optionDatasWithSubmenuTransform(
    partitionedToplevelOptions,
    (submenuSection: Array<Optional<OptionData>>) => {
      const [matchingSubOptions, notMatchingSubOptions] = _.partition(submenuSection, partitionFunc);
      return [...matchingSubOptions, ...notMatchingSubOptions];
    }
  );
}

/** @package */
export function _getAllNestedSelectableOptions(
  optionData: OptionData,
  includeParentOptionNameInSubOptionName: boolean,
  parentName?: string
): Array<OptionData> {
  const result: Array<OptionData> = [];
  if (optionData.submenu) {
    // eslint-disable-next-line no-restricted-syntax
    for (const subOptionData of _.flatten(optionData.submenu)) {
      const childOptionName = getOptionDataPureTextName(optionData);
      const subOptionName = parentName ? `${parentName} / ${childOptionName}` : `${childOptionName}`;
      result.push(
        ..._getAllNestedSelectableOptions(
          subOptionData,
          includeParentOptionNameInSubOptionName,
          subOptionName
        )
      );
    }
  } else if (isOptionSelectable(optionData)) {
    const childOptionName = getOptionDataPureTextName(optionData);
    const newSubOptionData = {
      ...optionData,
      name: includeParentOptionNameInSubOptionName ? `${parentName} / ${childOptionName}` : childOptionName,
    };
    result.push(newSubOptionData);
  }
  return result;
}

/*
 * Returns optionDatas that match a given query. Searches submenu options as well (if they exist).
 */
export function getMatchingOptionDatas(
  query: string,
  optionDatas: Array<OptionData>,
  {includeParentOptionNameInSubOptionName = true}: {includeParentOptionNameInSubOptionName?: boolean} = {}
): Array<OptionData> {
  if (query === '') {
    return optionDatas;
  }

  // If there is a search query, we automatically collapse submenus and only show the optionDatas that match
  // the search query
  const collapsedOptionDatas = [];
  for (const optionData of optionDatas) {
    if (optionData.submenu) {
      const flattenedSubmenu = _getAllNestedSelectableOptions(
        optionData,
        includeParentOptionNameInSubOptionName
      );
      for (const subOptionData of flattenedSubmenu) {
        if (!isOptionSelectable(subOptionData)) {
          continue;
        }
        if (strUtil.caseInsensitiveContains(getOptionDataPureTextName(subOptionData), query.trim())) {
          collapsedOptionDatas.push(subOptionData);
        }
      }
    } else if (
      isOptionSelectable(optionData) &&
      strUtil.caseInsensitiveContains(getOptionDataPureTextName(optionData), query.trim())
    ) {
      collapsedOptionDatas.push(optionData);
    }
  }
  return collapsedOptionDatas;
}

export function hasSubmenus(items: Array<OptionData>): boolean {
  return items.some((item) => item.submenu);
}
